import {NotificationCustomPosition, NotificationEdge, UserSettings} from "@/settings/interfaces";

export interface WindowRectangle {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface WindowPoint {
    x: number;
    y: number;
}

/** Display information provided by the main process. */
export interface DisplayInfo {
    bounds: WindowRectangle;
}

/** Cursor and display context provided by the main process. */
export interface ScreenContext {
    cursor: WindowPoint;
    display: DisplayInfo;
}

/**
 * Open notification window width: 350px of visible drawer (strip + panel),
 * plus a transparent corridor wide enough for the fully slid-out Vitale tab
 * and for the ADAC answer, 248px wide, beside the panel. Fixed on purpose:
 * resizing the window on tab hover repaints the transparent window (very
 * visible on Windows) and invalidates the hover mid-flight.
 */
export const NOTIFICATION_WINDOW_WIDTH = 562;
/** Visible thickness of the retracted tab (the collapsed window width). */
export const COLLAPSED_WIDTH = 35;

/**
 * Same-origin channel announcing a drag & drop placement to the main window
 * renderer. Not the `to-main-window` IPC relay: the shell shows the main
 * window on every message it relays, which popped it up at each drop.
 */
export const NOTIFICATION_PLACEMENT_CHANNEL = 'notification-placement';

/**
 * Offsets, as a fraction of the display height, matching the legacy position presets.
 */
const legacyPositionOffsets: Record<string, number> = {
    'top-edge': 0,
    'top-right': 0.25,
    'middle-right': 0.5,
    'bottom-right': 0.75,
    'bottom-edge': 1,
};

/**
 * Whether a stored value is a supported edge. Stored settings outlive
 * releases: an unknown edge (e.g. 'top', written by the abandoned top-edge
 * drag mode) must fall back to the default placement, never reach the UI.
 */
export function isNotificationEdge(value: unknown): value is NotificationEdge {
    return value === 'left' || value === 'right';
}

/**
 * Resolve the notification window placement from the user settings.
 *
 * Legacy position presets are mapped to the right edge, so settings saved
 * before drag & drop positioning keep their behavior.
 */
export function resolvePlacement(settings?: UserSettings | null): NotificationCustomPosition {
    const notification = settings?.notification;
    const custom = notification?.position === 'custom' ? notification.customPosition : undefined;

    if (custom && isNotificationEdge(custom.edge)) {
        return {
            edge: custom.edge,
            offset: clamp(custom.offset, 0, 1),
        };
    }

    return {
        edge: 'right',
        offset: legacyPositionOffsets[notification?.position ?? 'bottom-right'] ?? 0.75,
    };
}

/**
 * Compute the window bounds for a placement on a display: pinned to the left
 * or right display edge, centered on the offset along it (clamped on screen).
 */
export function expandedBounds(
    display: DisplayInfo,
    placement: NotificationCustomPosition,
    size: { width: number, height: number }
): WindowRectangle {
    const {x: dx, y: dy, width: dw, height: dh} = display.bounds;

    return {
        x: placement.edge === 'left' ? dx : dx + dw - size.width,
        y: clamp(dy + placement.offset * dh - size.height / 2, dy, dy + dh - size.height),
        width: size.width,
        height: size.height,
    };
}

/**
 * Compute the retracted tab bounds anchored to where the drawer strip
 * currently is on screen, so the tab does not jump when the drawer closes.
 */
export function collapsedAnchoredBounds(
    display: DisplayInfo,
    edge: NotificationEdge,
    expanded: WindowRectangle,
    size: { width: number, height: number }
): WindowRectangle {
    return {
        x: edge === 'left'
            ? display.bounds.x
            : display.bounds.x + display.bounds.width - size.width,
        y: expanded.y,
        ...size,
    };
}

/**
 * Compute the placement the window would snap to when the drag is released.
 *
 * The edge is chosen from the cursor position (nearest of left or right), the
 * offset from the dragged window center, so the tab lands where the user dropped it.
 */
export function snapPlacement(
    cursor: WindowPoint,
    windowBounds: WindowRectangle,
    display: DisplayInfo
): NotificationCustomPosition {
    const {x: dx, y: dy, width: dw, height: dh} = display.bounds;

    const edge: NotificationEdge = (cursor.x - dx) < (dx + dw - cursor.x) ? 'left' : 'right';
    const offset = (windowBounds.y + windowBounds.height / 2 - dy) / dh;

    return {edge, offset: clamp(offset, 0, 1)};
}

/** Clamp a value between min and max. */
function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), Math.max(min, max));
}
